import { Address } from '@yandex-int/k-common/typings';
import { InputChangeEvent, SimpleCallback, useIntl } from 'platform-components';
import { isValidHumanName } from 'platform-components/src/utils/input-validators';
import { validateEmail as validateEmailUtil } from 'platform-components/utils';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useMount } from 'react-use';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';

import * as certificateActions from './actions';
import { CertificateModal } from './certificate-modal';
import * as certificateSelectors from './selectors';
import { TextData, ImageData, CertificateModalStore } from './typings';
import { splitFullName } from './utils';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface OwnProps {
    canCancelSuggestion: boolean;
    imageData: ImageData;
    textData: TextData;
    shouldGenerateImmediately: boolean;
    ownCertificateSlug?: string;
    closeModal?: SimpleCallback;
}
interface Props extends StoreProps, DispatchProps, OwnProps {}

const CertificateModalContainerComponent = (props: Props) => {
    const {
        certificateSlug,
        setCertificateSectionRequest,
        shouldSetCertificateSection,
        getLocalities,
        acceptSuggestion,
        shouldGenerateImmediately,
        closeSuggestion,
        canCancelSuggestion,
        cancelSuggestion,
        canChangeData,
        localities,
        textData,
        closeModal,
        hasAgreedToReceiveCertificate,
        ...otherProps
    } = props;
    const intl = useIntl();
    const [fullName, setFullName] = useState('');
    const [fullNameErrorMessage, setFullNameErrorMessage] = useState<string | null>(null);
    const [locality, setLocality] = useState<Address | null>(null);
    const [localityErrorMessage, setLocalityErrorMessage] = useState<string | null>(null);
    const [school, setSchool] = useState<string>('');
    const [schoolErrorMessage, setSchoolErrorMessage] = useState<string | null>(null);
    const [email, setEmail] = useState<string>(props.email);
    const [emailErrorMessage, setEmailErrorMessage] = useState<string | null>(null);

    useMount(() => {
        if (shouldSetCertificateSection && certificateSlug) {
            setCertificateSectionRequest(certificateSlug);
        }
    });

    if (!certificateSlug) {
        return null;
    }

    const handleFullNameChange = ({ target: { value } }: InputChangeEvent) => {
        const fullNameStr = value.replace(/\s+/g, ' ');
        setFullName(fullNameStr);
        setFullNameErrorMessage(null);
    };

    const handleLocalityChange = (localityData: Address | null) => {
        setLocality(localityData);
        setLocalityErrorMessage(null);
    };

    const handleLocalityInputChange = (phrase: string) => {
        if (locality) {
            handleLocalityChange(null);
        }

        getLocalities(phrase ? phrase.trim() : phrase);
    };

    const handleSchoolChange = ({ target: { value } }: InputChangeEvent) => {
        setSchool(value);
        setSchoolErrorMessage(null);
    };

    const handleEmailChange = ({ target: { value } }: InputChangeEvent) => {
        setEmail(value);
        setEmailErrorMessage(null);
    };

    const handleAcceptClick = () => {
        if (!validateForm() || !certificateSlug) {
            return;
        }

        acceptSuggestion(certificateSlug, shouldGenerateImmediately, getFormData());
    };

    const handleClose = () => {
        if (closeModal) {
            closeModal();
        } else {
            closeSuggestion(certificateSlug);
        }
    };

    const handleCancel = () => {
        if (closeModal) {
            closeModal();
        } else if (canCancelSuggestion) {
            cancelSuggestion(certificateSlug);
        } else {
            closeSuggestion(certificateSlug);
        }
    };

    const handleValidateFullName = () => {
        let fullNameErrorMessageId: string | null = null;

        if (!fullName) {
            fullNameErrorMessageId = 'certificatesModal.form.fio.required';
        } else if (!isValidHumanName(fullName)) {
            fullNameErrorMessageId = 'certificatesModal.form.fio.invalid';
        }

        const fullNameErrorMessageStr = prepareMessage(fullNameErrorMessageId);
        setFullNameErrorMessage(fullNameErrorMessageStr);

        return !fullNameErrorMessage;
    };

    const handleValidateLocality = () => {
        const localityErrorMessageStr = prepareMessage(!locality ? 'certificatesModal.form.locality.required' : null);

        setLocalityErrorMessage(localityErrorMessageStr);

        return !localityErrorMessage;
    };

    const handleValidateSchool = () => {
        const schoolErrorMessageStr = prepareMessage(!school ? 'certificatesModal.form.school.required' : null);

        setSchoolErrorMessage(schoolErrorMessageStr);

        return !schoolErrorMessage;
    };

    const handleValidateEmail = () => {
        let emailErrorMessageId: string | null = null;

        if (!email) {
            emailErrorMessageId = 'certificatesModal.form.email.required';
        } else if (!validateEmailUtil(email)) {
            emailErrorMessageId = 'certificatesModal.form.email.invalid';
        }

        setEmailErrorMessage(prepareMessage(emailErrorMessageId));

        return !emailErrorMessageId;
    };

    const validateForm = () => {
        let isValid;

        // Запустакем проверку всех полей и собирается результат в isValid.
        // Объединять валидаторы через && нельзя, т.к. тогда могут отработать не все валидаторы
        isValid = handleValidateEmail();
        isValid = handleValidateFullName() && isValid;
        isValid = handleValidateLocality() && isValid;
        isValid = handleValidateSchool() && isValid;

        return isValid;
    };

    const isFormValid = () => {
        return (
            email &&
            !emailErrorMessage &&
            school &&
            !schoolErrorMessage &&
            locality &&
            !localityErrorMessage &&
            fullName &&
            !fullNameErrorMessage
        );
    };

    const getFormData = () => {
        return {
            email,
            locality: locality?.title,
            school,
            ...splitFullName(fullName),
        };
    };

    const prepareMessage = (messageId: string | null) => {
        return messageId ? intl.formatMessage({ id: messageId }) : null;
    };

    return (
        <CertificateModal
            canCancelSuggestion={canCancelSuggestion}
            canChangeData={canChangeData}
            emailData={{ email, emailErrorMessage, handleEmailChange, handleValidateEmail }}
            fullNameData={{ fullName, fullNameErrorMessage, handleFullNameChange, handleValidateFullName }}
            handleAcceptClick={handleAcceptClick}
            handleCancel={handleCancel}
            handleClose={handleClose}
            hasAgreedToReceiveCertificate={hasAgreedToReceiveCertificate}
            isButtonDisabled={!canChangeData || !isFormValid()}
            localityData={{
                localities,
                locality,
                localityErrorMessage,
                handleLocalityInputChange,
                handleLocalityChange,
                handleValidateLocality,
            }}
            schoolData={{ school, schoolErrorMessage, handleSchoolChange, handleValidateSchool }}
            textData={{ ...CertificateModalContainerComponent.defaultProps.textData, ...textData }}
            {...otherProps}
        />
    );
};

const mapStateToProps = <T extends { certificateModal: CertificateModalStore }>(storeState: T, ownProps: OwnProps) => {
    const {
        getHasAgreedToReceiveCertificate,
        canChangeData,
        getEmail,
        getFirstSuggestedCertificateSlug,
        getLocalities,
        getStatusOfCertificateSection,
        getShouldSetCertificateSection,
    } = certificateSelectors;

    const certificateSlug = ownProps.ownCertificateSlug || getFirstSuggestedCertificateSlug(storeState);

    return {
        canChangeData: canChangeData(storeState),
        shouldSetCertificateSection: getShouldSetCertificateSection(storeState, { slug: certificateSlug }),
        certificateSectionStatus: getStatusOfCertificateSection(storeState),
        email: getEmail(storeState),
        certificateSlug,
        hasAgreedToReceiveCertificate: getHasAgreedToReceiveCertificate(storeState, { slug: certificateSlug }),
        localities: getLocalities(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
    const { acceptSuggestion, cancelSuggestion, closeSuggestion, getLocalities, setCertificateSectionRequest } =
        certificateActions;

    return bindActionCreators(
        {
            acceptSuggestion,
            cancelSuggestion,
            closeSuggestion,
            getLocalities,
            setCertificateSectionRequest,
        },
        dispatch
    );
};

CertificateModalContainerComponent.defaultProps = {
    shouldGenerateImmediately: false,
    textData: {
        acceptButtonId: 'certificatesModal.form.accept',
        cancelButtonId: 'certificatesModal.form.remindLater',
    },
    toastData: {
        headerId: 'toast.certificatesModal.success.header',
        messageId: 'toast.certificatesModal.success.message',
    },
};

export const CertificateModalContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(CertificateModalContainerComponent);
